import { Text } from "~/components/Text"
import { FullscreenOutlined, MutedOutlined } from "@ant-design/icons"
import { Button, Flex, Popover, Slider } from "antd"
import { formatTime } from "~/common/utils"
import styles from "./Controls.css"

const getPopupContainer = (trigger: HTMLElement): HTMLElement =>
  trigger.closest<HTMLElement>(":fullscreen") ||
  trigger.closest<HTMLElement>('[role="dialog"]') ||
  trigger.ownerDocument.body

const formatPlayerTime = (
  seconds: number,
  precision: "seconds" | "centiseconds",
) => {
  if (precision === "seconds") return formatTime(seconds, 0)

  const totalCentiseconds = Math.max(0, Math.round(seconds * 100))
  const minutes = Math.floor(totalCentiseconds / 6000)
  const remainingCentiseconds = totalCentiseconds - minutes * 6000
  const wholeSeconds = Math.floor(remainingCentiseconds / 100)
  const centiseconds = remainingCentiseconds - wholeSeconds * 100

  return `${minutes}:${String(wholeSeconds).padStart(2, "0")}:${String(centiseconds).padStart(2, "0")}`
}

type ControlsProps = {
  duration: number
  elapsed: number
  muted: boolean
  volume: number
  disabled: boolean
  timePrecision: "seconds" | "centiseconds"
  onFullscreen: () => void
  onSeek: (value: number) => void
  onVolumeChange: (value: number) => void
}

export const Controls = ({
  duration,
  elapsed,
  muted,
  volume,
  disabled,
  timePrecision,
  onFullscreen,
  onSeek,
  onVolumeChange,
}: ControlsProps) => (
  <Flex
    className={styles.controls}
    gap={4}
    vertical
    onClick={(event) => event.stopPropagation()}
  >
    <Flex
      align="center"
      justify="space-between"
      gap={8}
    >
      <Text
        size="s"
        color="var(--ant-color-text-light-solid)"
        className={styles.time}
      >
        {formatPlayerTime(elapsed, timePrecision)} {" / "}
        {formatPlayerTime(duration, timePrecision)}
      </Text>
      <Flex
        gap={4}
        align="center"
      >
        <Popover
          arrow={false}
          getPopupContainer={getPopupContainer}
          classNames={{ container: styles.volumePopover }}
          content={
            <Slider
              aria-label="视频音量"
              className={styles.volumeSlider}
              max={1}
              min={0}
              step={0.05}
              tooltip={{
                getPopupContainer,
                formatter: (value) =>
                  `${Math.round(Math.max(0, value || 0) * 100)}%`,
                placement: "left",
              }}
              value={muted ? 0 : volume}
              vertical
              onChange={onVolumeChange}
            />
          }
          placement="top"
          trigger="click"
        >
          <Button
            aria-label="调节视频音量"
            className={styles.soundButton}
            icon={<MutedOutlined className={muted && styles.soundIconMuted} />}
            size="small"
            type="text"
          />
        </Popover>
        <Button
          aria-label="切换全屏"
          className={styles.soundButton}
          icon={<FullscreenOutlined />}
          size="small"
          type="text"
          onClick={onFullscreen}
        />
      </Flex>
    </Flex>
    <Slider
      aria-label="视频播放进度"
      className={styles.progress}
      disabled={disabled || duration <= 0}
      max={Math.max(0.1, duration)}
      min={0}
      step={0.1}
      value={elapsed}
      onChange={onSeek}
    />
  </Flex>
)
